import { useCallback, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { ReusableBlock } from "@/react/hooks/api/useReusableBlocks";
import { useDeleteReusableBlock } from "@/react/hooks/api/useReusableBlocks";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Typography } from "@circle-react-uikit/Typography";
import { ConfirmationModal } from "../../ModalsV2/ConfirmationModal";
import { ReusableBlockCover } from "./ReusableBlockCover";

export interface ReusableBlockPreviewProps {
  block: ReusableBlock;
}

export const ReusableBlockPreview = ({ block }: ReusableBlockPreviewProps) => {
  const [shouldShowDeleteConfirmation, setShouldShowDeleteConfirmation] =
    useState(false);

  const { addBlocksToRoot, closeSidebarView, dragAndDropType } =
    useBuilderStore(state => ({
      addBlocksToRoot: state.addBlocksToRoot,
      closeSidebarView: state.closeSidebarView,
      dragAndDropType: state.dragAndDropType,
    }));

  const { mutate: deleteBlock } = useDeleteReusableBlock({
    onSuccess: () => {
      setShouldShowDeleteConfirmation(false);
    },
  });

  const isDraggingEnabled = dragAndDropType !== "disabled";

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `reusable-${block.id}`,
    data: {
      type: "component" as const,
      defaultData: block.block_data,
      meta: {
        name: block.name,
        version: 1,
        category: [],
      },
      Block: () => null,
    },
  });

  const handleAddBlock = useCallback(() => {
    if (!isDragging) {
      addBlocksToRoot(block.block_data, undefined, true);
      closeSidebarView();
    }
  }, [addBlocksToRoot, closeSidebarView, block.block_data, isDragging]);

  const handleDeleteClick = useCallback(() => {
    setShouldShowDeleteConfirmation(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteBlock(block.id);
  }, [deleteBlock, block.id]);

  const dropdownOptions = [
    {
      label: t("delete"),
      value: "delete",
      color: "text-v2-danger",
      onClick: handleDeleteClick,
    },
  ];

  return (
    <div className="space-y-2.5">
      <button
        ref={setNodeRef}
        type="button"
        onClick={handleAddBlock}
        {...listeners}
        {...attributes}
        className={classNames(
          "border-primary bg-secondary hover:border-dark hover:bg-secondary focus:border-dark focus:bg-secondary text-dark relative flex w-full flex-col items-center gap-2 overflow-hidden rounded-lg border transition-colors duration-150 ease-linear",
          {
            "cursor-grab": isDraggingEnabled,
            "cursor-grabbing": isDragging,
            "cursor-pointer": !isDraggingEnabled && !isDragging,
          },
        )}
      >
        <div className="text-disabled flex h-[160px] w-[304px] items-center justify-center">
          <ReusableBlockCover />
        </div>
      </button>

      <div className="flex items-center justify-between">
        <Typography.LabelSm color="text-dark" truncate weight="medium">
          {block.name}
        </Typography.LabelSm>
        <Dropdown
          direction="bottom-end"
          menuWrapperClassName="!mt-2"
          buttonClassName="flex items-center h-6 w-6 justify-center !rounded-full transition-colors duration-200 hover:bg-tertiary focus-visible:outline-secondary focus-visible:rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:bg-tertiary"
          button={
            <IconButton
              name="20-menu-dots-horizontal"
              iconSize={20}
              buttonSize={24}
              ariaLabel={t("actions")}
            />
          }
          options={dropdownOptions}
        />
      </div>

      <ConfirmationModal
        isOpen={shouldShowDeleteConfirmation}
        onClose={() => setShouldShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title={t("builder_ui.block_list.delete_reusable_block")}
        confirmText={t("delete")}
        confirmVariant="danger"
      >
        <p>
          {t("builder_ui.block_list.delete_reusable_block_confirmation", {
            name: block.name,
          })}
        </p>
      </ConfirmationModal>
    </div>
  );
};
